import { useEffect, useRef, useState, type ReactNode } from "react"
import Popover from "@/v2-ui/popover"
import {
  POPOVER_CONTENT_WIDTH_MD,
  POPOVER_LIST_PREFERRED_HEIGHT,
} from "@/v2-ui/popover/popover.constants"
import {
  resolvePopoverOpenAlign,
  resolvePopoverOpenSide,
  type PopoverResolvedAlign,
  type PopoverResolvedSide,
} from "@/v2-ui/popover/popover.utils"
import SelectorContext, { type SearchStatus } from "./selector.context"

/** Matches `sideOffset` on Popover.Content (PopoverBase default). */
const SELECTOR_SIDE_OFFSET = 4

export type SelectorProps = {
  open?: boolean
  onOpenChange?(open: boolean): void
  shouldFilter?: boolean
  isLoading?: boolean
  children: ReactNode
}

/**
 * A searchable pick list in a popover. The list's height changes with
 * every keystroke (results arrive, results filter away), so its placement
 * is decided once, when it opens, from where the trigger sits: Radix's own
 * collision handling would re-run on every resize and hop the list between
 * above and below the trigger while the operator is aiming at an item.
 */
function Selector(props: SelectorProps) {
  const {
    open,
    onOpenChange: onOpenChangeProp,
    shouldFilter = false,
    isLoading = false,
    children,
  } = props

  const [_isLoading, setIsLoading] = useState(isLoading ?? false)
  const [isOpen, setIsOpen] = useState(open ?? false)
  const [searchStatus, setSearchStatus] = useState<SearchStatus>("idle")
  const minSearchLengthRef = useRef(0)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [side, setSide] = useState<PopoverResolvedSide>("bottom")
  const [align, setAlign] = useState<PopoverResolvedAlign>("start")

  function placeContent() {
    const trigger = triggerRef.current
    if (!trigger) return
    setSide(
      resolvePopoverOpenSide({
        trigger,
        sideOffset: SELECTOR_SIDE_OFFSET,
        preferredHeight: POPOVER_LIST_PREFERRED_HEIGHT,
      }),
    )
    setAlign(
      resolvePopoverOpenAlign({
        trigger,
        contentWidth: POPOVER_CONTENT_WIDTH_MD,
        align: "start",
      }),
    )
  }

  useEffect(() => {
    if (open === undefined) return
    // A controlled open (not via the trigger) still needs its placement.
    if (open) placeContent()
    setIsOpen(open)
  }, [open])

  return (
    <SelectorContext.Provider
      value={{
        shouldFilter,
        isLoading: _isLoading,
        setIsLoading,
        searchStatus,
        setSearchStatus,
        minSearchLengthRef,
        triggerRef,
        side,
        align,
      }}
    >
      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          if (open) placeContent()
          setIsOpen(open)
          onOpenChangeProp?.(open)
        }}
      >
        {children}
      </Popover>
    </SelectorContext.Provider>
  )
}

export default Selector
